import React, { FC } from 'react';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import Breadcrumb from '@ui/atoms/Breadcrumb';
import { CenteredLink } from '@ui/atoms/CenteredLink';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import BillCard from '@ui/organisms/BillCard';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { IMemberInvoiceProposal } from '@hooks/useMemberInvoiceProposal';

import { IPageProps } from '@folx-types/flow-steps';

export interface IMedicationBillsDetailStepContent {
	title: string;
	breadcrumbLabel: string;
	paidBillLabel: string;
	paidBillDisclaimer: string;
	unpaidBillDisclaimer: string;
	expiredBillDisclaimer: string;
	unpaidBillShipping: string;
	billNotFoundTitle: string;
	billNotFoundText: string;
	billNotFoundButtonText: string;
}

interface IMedicationBillsDetailStepProps extends IPageProps {
	pageContent: IMedicationBillsDetailStepContent;
	detailMedicationBill?: IMemberInvoiceProposal;
	medicationBillId?: string;
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);

interface IBillNotFoundProps {
	title: string;
	text: string;
	buttonText: string;
	medicationBillId?: string;
}
const BillNotFound = ({ title, text, buttonText, medicationBillId }: IBillNotFoundProps) => (
	<SpacedColumn gap="md">
		<SpacedColumn gap="xxs">
			<BasicText variant="fineprint" color="placeholderGray" tint="darken40">
				{medicationBillId}
			</BasicText>
			<BaseCard
				bgColor="white"
				body={
					<SpacedColumn gap="md">
						<HeadingText>{title}</HeadingText>
						<BasicText variant="body">{text}</BasicText>
						<CenteredLink to="../../" variant="underline">
							{buttonText}
						</CenteredLink>
					</SpacedColumn>
				}
				borderColor="red"
				radius="md"
				shadow
			/>
		</SpacedColumn>
	</SpacedColumn>
);

const MedicationBillsDetailStep: FC<IMedicationBillsDetailStepProps> = ({
	pageContent,
	detailMedicationBill,
	medicationBillId,
}) => {
	const {
		title,
		breadcrumbLabel,
		paidBillLabel,
		paidBillDisclaimer,
		unpaidBillShipping,
		unpaidBillDisclaimer,
		expiredBillDisclaimer,
		billNotFoundTitle,
		billNotFoundText,
		billNotFoundButtonText,
	} = pageContent;
	return (
		<CenteredGridItem>
			<SpacedColumn gap="sm" marginBottom="lg">
				<Breadcrumb label={breadcrumbLabel} href="../.." />
				{detailMedicationBill ? (
					<SpacedColumn gap="md">
						<HeadingText>{title}</HeadingText>
						<SpacedColumn gap="xxs">
							<BasicText variant="fineprint" color="placeholderGray" tint="darken40">
								{medicationBillId}
							</BasicText>
							<BaseCard
								bgColor="white"
								body={
									<BillCard
										bill={detailMedicationBill}
										paidBillLabel={paidBillLabel}
										paidBillDisclaimer={paidBillDisclaimer}
										unpaidBillShipping={unpaidBillShipping}
										unpaidBillDisclaimer={unpaidBillDisclaimer}
										expiredBillDisclaimer={expiredBillDisclaimer}
									/>
								}
								borderless
								radius="md"
								shadow
							/>
						</SpacedColumn>
					</SpacedColumn>
				) : (
					<BillNotFound
						title={billNotFoundTitle}
						text={billNotFoundText}
						buttonText={billNotFoundButtonText}
						medicationBillId={medicationBillId}
					/>
				)}
			</SpacedColumn>
		</CenteredGridItem>
	);
};

export default MedicationBillsDetailStep;
